import Cookies from 'js-cookie';
import { gql, Locale, makeUserAnalyticsData, UnreachableCaseError } from '@yi/core';
import { MembershipTierWithAnon } from '@yi/core/src/types/MembershipTierWithAnon';

import { UserData, PageViewData } from '~/types';

import { EVENT_NAMES, TEST_EVENT_NAMES } from './constants';
import { TrackOpts } from './events';
import { getAnonymousId } from './getAnonymousId';
import { setFacebookPixelFormFieldsData } from './setFacebookPixelFormFieldsData';
import { track } from './track';
import {
  CoursePurchaseEventProps,
  getCourseProductsFromOrder,
  getProductProps,
  getShipstationProperties,
  getTierInfoFromOffer,
  Offer,
  parseProductData,
  Product,
  AnalyticsEvent,
} from './utilities';
import { notifyBugsnag } from '../bugsnagClient';
import { getEnv } from '../getEnv';
import { logSearchInsightsEvent } from '../searchInsights';
import { getLocalizedDateFromISOString } from '../utilities';

export function setFacebookIds() {
  const fbc = Cookies.get('_fbc');
  const fbp = Cookies.get('_fbp');

  if (fbc) logAnalyticsData({ fbc });
  if (fbp) logAnalyticsData({ fbp });
}

export function logAnalyticsEvent(
  event: AnalyticsEvent,
  currentUser?: gql.typesClient.Query_CurrentUser['CurrentUser'],
  tier?: MembershipTierWithAnon,
) {
  const anonymousId = getAnonymousId();
  const simpleUserTraits = window.segmentData
    ? {
        traits: {
          email: window.segmentData.email || undefined,
          firstName: window.segmentData.firstName || undefined,
          lastName: window.segmentData.lastName || undefined,
          phone: window.segmentData.phone || undefined,
        },
      }
    : undefined;

  try {
    const simpleUserTraitsContext: TrackOpts = {
      context: { ...simpleUserTraits },
      integrations: { Optimizely: false },
    };
    const optimizelyIntegrationAndSimpleUserTraits: TrackOpts = {
      context: { ...simpleUserTraits },
      integrations: { Optimizely: { userId: anonymousId } },
    };
    switch (event.event) {
      case EVENT_NAMES.GATSBY_PAGEVIEW:
        if (window.segmentData.userId) {
          window.analytics.identify(window.segmentData.userId, {
            ...window.segmentData,
            ...(currentUser ? makeUserAnalyticsData(currentUser, tier || null) : undefined),
          });
        }
        window.analytics.page({ ...window.segmentData }, { context: { ...simpleUserTraits } });

        track(EVENT_NAMES.GATSBY_PAGEVIEW, { ...window.segmentData }, simpleUserTraitsContext);

        if (
          window.segmentData.memberType === 'Digital' ||
          window.segmentData.memberType === 'Premium'
        ) {
          track('ActiveMember', { ...window.segmentData }, simpleUserTraitsContext);
        }
        break;
      case 'memberOfferHit':
        track('MemberOfferHit', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'changedPlan':
        track(
          'Changed Plan',
          { ...parseProductData(event), ...event, ...window.segmentData },
          simpleUserTraitsContext,
        );
        break;
      case 'membershipSignup':
      case 'test/membershipSignup':
        logSearchInsightsEvent({
          type: 'convertedObjectIDsAfterSearch',
          eventName: 'Membership Signup',
        });
        track(
          getEnv() !== 'production' ? TEST_EVENT_NAMES.SIGNED_UP : EVENT_NAMES.SIGNED_UP,
          { ...parseProductData(event), ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );

        // Order Completed event for Google Analytics and Friendbuy only
        track(
          getEnv() !== 'production'
            ? TEST_EVENT_NAMES.ORDER_COMPLETED
            : EVENT_NAMES.ORDER_COMPLETED,
          { ...parseProductData(event), ...event, ...window.segmentData },
          {
            integrations: {
              All: false,
              'Google Analytics': true,
              'Google Tag Manager': true,
              'Facebook Pixel': true,
              'Facebook Conversions API': true,
              FriendBuy: true,
            },
            context: { ...simpleUserTraits },
          },
        );
        break;
      case 'checkout':
        track(
          'Checkout Started',
          { ...parseProductData(event), ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'paymentInfo':
        track(
          'Payment Info Entered',
          { ...parseProductData(event), ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'coursePurchase':
        logSearchInsightsEvent({
          type: 'convertedObjectIDsAfterSearch',
          eventName: 'Course Purchase',
        });
        track(
          EVENT_NAMES.ORDER_COMPLETED,
          { ...parseProductData(event), ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'addToCart':
        track(
          'Product Added',
          { ...parseProductData(event), ...event, ...window.segmentData, anonymousId },
          simpleUserTraitsContext,
        );
        break;
      case 'annualUpgrade':
        track(
          'AnnualUpgrade',
          { ...event, ...window.segmentData },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'logoutStateChange':
        track('logoutStateChange', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'logoutButtonClick':
        track('Signed Out', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        // user has logged out so reset analytics
        window.analytics.reset();
        break;
      case 'login':
        track('Signed In', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'experimentViewed':
        track('Experiment Viewed', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'emailCapture':
        if (event.email) {
          window.analytics.identify({ email: event.email });
        }
        track(
          EVENT_NAMES.LEAD_CAPTURED,
          { ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'accountCreation':
        track(
          'Account Created',
          { ...event, ...window.segmentData, anonymousId },
          optimizelyIntegrationAndSimpleUserTraits,
        );
        break;
      case 'test/LiveRegistration':
        track(
          'test/LiveRegistration',
          { ...event, ...window.segmentData },
          simpleUserTraitsContext,
        );
        break;
      case 'liveRegistration':
        track('liveRegistration', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'test/LiveCancelation':
        track('test/LiveCancelation', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'liveCancelation':
        track('liveCancelation', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'addToCalendar':
        track('Calendar Event Added', { ...event, ...window.segmentData }, simpleUserTraitsContext);
        break;
      case 'exitedPracticeParty':
        track(
          'Exited Practice Party',
          { ...event, ...window.segmentData },
          simpleUserTraitsContext,
        );
        break;
      default:
        notifyBugsnag(new UnreachableCaseError(event));
    }
  } catch (e) {
    console.error(e);
    notifyBugsnag(e);
  }
}

type Data = UserData & PageViewData;
export function logAnalyticsData(data: Data) {
  window.segmentData = { ...window.segmentData, ...data };
}

export function sendEmailCaptureEvent(email: string) {
  setFacebookPixelFormFieldsData({ email }); // must update before analytics event is sent
  logAnalyticsData({ email });
  logAnalyticsEvent({
    event: 'emailCapture',
    eventCategory: 'Email Capture',
    eventAction: 'Sign Up Pages',
    eventLabel: 'Sign Up Pages',
    eventNonInteraction: true,
    email,
    category: 'Email Capture',
    label: 'Email Capture',
  });
}

export function sendAddToCartEvent(product: Product) {
  const { products } = getProductProps(product);
  logAnalyticsEvent({
    event: 'addToCart',
    ...getProductProps(product),
    ecommerce: {
      add: {
        products,
      },
    },
  });
}

export function sendCheckoutEvent(product: Product) {
  const { products } = getProductProps(product);
  logAnalyticsEvent({
    event: 'checkout',
    ...getProductProps(product),
    ecommerce: {
      checkout: {
        actionField: { step: 1 },
        products,
      },
    },
  });
}

export function sendPaymentInfoEvent(product: Product) {
  const { products } = getProductProps(product);
  logAnalyticsEvent({
    event: 'paymentInfo',
    ...getProductProps(product),
    ecommerce: {
      checkout: {
        actionField: { step: 2 },
        products,
      },
    },
  });
}

export type SignupEventProps = {
  created_at: any;
  next_assessment_at: any;
  next_assessment_amount: number | null | undefined;
  is_trial: boolean | undefined;
  status: gql.typesClient.SubscriptionStatus | undefined;
  isWinback: boolean;
};

export function sendMembershipSignupEvent(
  order: gql.typesClient.Mutate_CartCheckout_cartCheckout_order,
  offer: Offer | null,
  payment_type: string,
  signupProps: SignupEventProps,
) {
  const tierInfo = getTierInfoFromOffer(offer);
  const { products, productPrice } = tierInfo;
  const { isWinback, ...rest } = signupProps;

  try {
    logAnalyticsEvent({
      event: 'membershipSignup',
      ...tierInfo,
      ...rest,
      winback: isWinback,
      category: 'Member Signup',
      label: 'Member Signup',
      order_id: order.id,
      order_payment_type: payment_type,
      subscription_coupon_code: offer?.coupon_code,
      subscription_plan_id: offer?.plan_id,
      ecommerce: {
        purchase: {
          actionField: {
            id: order.id, // Transaction ID. Required.
            affiliation: '',
            revenue: productPrice, // Total transaction value
            tax: '0',
            shipping: '0',
            coupon: '',
          },
          products,
        },
      },
    });
  } catch (e) {
    notifyBugsnag(e);
  }
}

export function sendMemberPromoHitEvent(offer: Offer) {
  logAnalyticsEvent({
    event: 'memberOfferHit',
    memberOfferId: offer.plan_id,
    member_offer_id: offer.plan_id,
    ...getTierInfoFromOffer(offer),
  });
}

export function sendChangedPlanEvent(...args: Parameters<typeof getTierInfoFromOffer>) {
  logAnalyticsEvent({
    event: 'changedPlan',
    ...getTierInfoFromOffer(...args),
  });
}

export function sendAccountCreationEvent() {
  logAnalyticsEvent({
    event: 'accountCreation',
    category: 'Account Creation',
    eventAction: 'Account Creation',
    eventCategory: 'Account Creation',
    eventLabel: 'Account Creation',
    eventNonInteraction: true,
    label: 'Account Creation',
  });
}

const eventName = getEnv() !== 'production' ? 'test/LiveRegistration' : 'liveRegistration';

export function sendLiveRegistrationEvent(
  startDate: string,
  teacher: string,
  meetingUrl: string,
  title: string,
  locale: Locale,
  automationToken: string,
) {
  const startTime = new Date(startDate).getTime() - 1800000;
  if (startTime < Date.now()) return;
  logAnalyticsEvent({
    event: eventName,
    eventCategory: eventName,
    eventAction: eventName,
    category: eventName,
    eventLabel: eventName,
    eventNonInteraction: true,
    label: eventName,
    teacher,
    meetingUrl,
    title,
    automationToken,
    cancelationToken: 'token',
    startDate: `${new Date(startDate)}`,
    startTime: getLocalizedDateFromISOString(startDate, locale).timeText,
    timeUntil: `${new Date(startTime).toISOString()}` || '',
    ...window.segmentData,
  });
}

const cancelationEvent = getEnv() !== 'production' ? 'test/LiveCancelation' : 'liveCancelation';
export function sendLiveCancelation(cancelationToken: string) {
  logAnalyticsEvent({
    event: cancelationEvent,
    eventCategory: cancelationEvent,
    eventAction: cancelationEvent,
    category: cancelationEvent,
    eventLabel: cancelationEvent,
    eventNonInteraction: true,
    label: cancelationEvent,
    automationToken: cancelationToken,
    cancelationToken,
    timeUntil: new Date(Date.now() * 2).toISOString(),
  });
}

export function sendLoginEvent() {
  logAnalyticsEvent({
    event: 'login',
    category: 'Account',
    eventAction: 'login',
    eventCategory: 'Account',
    eventLabel: 'login',
    eventNonInteraction: true,
    label: 'login',
  });
}

export function sendAnnualUpgradeEvent(...args: Parameters<typeof getTierInfoFromOffer>) {
  logAnalyticsEvent({
    event: 'annualUpgrade',
    ...getTierInfoFromOffer(...args),
    category: 'Annual Upgrade',
    eventAction: 'Annual Upgrade',
    eventCategory: 'Annual Upgrade',
    eventLabel: 'Annual Upgrade',
    eventNonInteraction: true,
    label: 'Annual Upgrade',
  });
}

export function sendCoursePurchaseEvent(props: CoursePurchaseEventProps) {
  const { order, type, current_user, variables, hasAddon, addons, bundle_id, bundle_name } = props;

  const courseProducts = getCourseProductsFromOrder(order);

  let shipstation = null;

  if (hasAddon && order && current_user && variables && addons) {
    shipstation = getShipstationProperties(order, current_user, variables, addons);
  }

  const productName = bundle_name ? bundle_name : order.items?.[0]?.title;
  const productId = bundle_id ? bundle_id : order.items?.[0]?.id;

  if (productName && productId && order.total) {
    logAnalyticsEvent({
      event: 'coursePurchase',
      category: 'Order Completed',
      label: 'Order Completed',
      productType: bundle_id ? 'bundle' : 'course',
      productName,
      productId,
      productPrice: order.total,
      order_id: order.id,
      order_payment_type: type,
      products: courseProducts,
      shipstation,
      ecommerce: {
        purchase: {
          actionField: {
            id: order.id, // Transaction ID. Required.
            affiliation: '',
            revenue: order.total, // Total transaction value
            tax: '0',
            shipping: '0',
            coupon: order.coupon,
          },
          products: courseProducts,
        },
      },
    });
  } else {
    notifyBugsnag('productName or productId are missing!');
  }
}
